<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { useLogger } from 'vue-logger-plugin'
import { ref, onMounted, computed } from 'vue'
import { setStoreLogger } from '@/logger.ts'
import { Grid, type GridSortChangeEvent } from '@progress/kendo-vue-grid'
import { GetEventListByHost } from '@/api/Event.ts'
import type { IEvent } from '@/types/EventTypes'
import type { SortDescriptor } from '@progress/kendo-data-query'
import { orderBy } from '@progress/kendo-data-query'
import { formatDateWithShortMonthAndTime } from '@/api/Common.ts'
import { GetHostDashboardUrl } from '@/stores/SettingsStore.ts'
import { handleError } from '@/services/ErrorService.ts'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'
import { formatCityRegion } from '@/utils/locationHelper.ts'

const logger = useLogger()
setStoreLogger(logger)
logger.debug('HostEventList.vue')

const loader = ref<boolean>(false)
const sort = ref<SortDescriptor[]>([{ field: 'eventStartDate', dir: 'desc' }])
const gridData = ref<IEvent[]>([])
const sortedGridData = computed<IEvent[]>(() =>
  orderBy(gridData.value, sort.value),
)
const columns = [
  {
    field: 'title',
    title: t('eventList.EventTitle'),
    width: resolveColumnWidth('250px', t('eventList.EventTitle')),
    cell: 'eventTitleTemplate',
  },
  {
    field: 'eventId',
    title: t('eventList.EventId'),
    width: resolveColumnWidth('120px', t('eventList.EventId')),
  },
  {
    field: 'eventStartDate',
    title: t('eventList.EventDate'),
    width: resolveColumnWidth('180px', t('eventList.EventDate')),
    cell: 'eventDateTemplate',
  },
  {
    field: 'eventLocation',
    title: t('eventList.EventLocation'),
    width: resolveColumnWidth('250px', t('eventList.EventLocation')),
    cell: 'eventLocationTemplate',
  },
]

const hostDashboardURL = ref<string>('')

onMounted(async () => {
  logger.debug('onMounted')

  GetHostDashboardUrl().then(url => {
    hostDashboardURL.value = url + '?event='
  })

  GetOpenEventList()
})

function sortChangeHandler(e: GridSortChangeEvent) {
  loader.value = true
  if (e.sort[0] !== undefined) {
    sort.value = e.sort
  } else {
    logger.debug('sortChangeHandler: e.sort[0] is undefined')
  }
  loader.value = false
  HideNoRecordsWhileLoading()
}

function ReLoadGrid() {
  loader.value = true
  gridData.value = []
  sort.value = [{ field: 'eventStartDate', dir: 'desc' }]
  HideNoRecordsWhileLoading()
}

function HideNoRecordsWhileLoading() {
  const eventListDiv = document.getElementById(
    'eventListGridRowDiv',
  ) as HTMLElement
  const classAttribute = eventListDiv.attributes.getNamedItem('class')
  if (classAttribute) {
    if (loader.value == false) {
      classAttribute.value = 'row mt-3'
    } else {
      classAttribute.value = 'row mt-3 grid-loading'
    }
  }
}

function GetOpenEventList() {
  ReLoadGrid()
  GetEventListByHost({})
    .then(eventResp => {
      if (eventResp) {
        for (const event of eventResp.items) {
          gridData.value.push(event)
        }
        loader.value = false
        HideNoRecordsWhileLoading()
      }
    })
    .catch(async error => {
      await handleError(error, t)
    })
}
</script>

<template>
  <div class="hostEventList">
    <div>
      <div class="row">
        <div class="col-6">
          <p class="pageHeader">
            {{ t('hostEventList.Events') }}
          </p>
        </div>
      </div>
      <div id="eventListGridRowDiv" class="row mt-3">
        <div class="col-12">
          <Grid
            ref="grid"
            :data-items="sortedGridData"
            :resizable="true"
            :sortable="true"
            :sort="sort"
            :pageable="false"
            :columns="columns"
            @sortchange="sortChangeHandler"
            :loader="loader"
          >
            <template v-slot:eventTitleTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="eventTitleDiv">
                <a
                  class="eventTitleLink"
                  :href="hostDashboardURL + props.dataItem.eventIdToken"
                  >{{ props.dataItem.title }}</a
                >
              </td>
            </template>
            <template v-slot:eventDateTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'" class="text-center">
                {{
                  formatDateWithShortMonthAndTime(props.dataItem.eventStartDate)
                }}
              </td>
            </template>
            <template v-slot:eventLocationTemplate="{ props }">
              <td v-if="props.rowType !== 'groupHeader'">
                {{
                  props.dataItem.address != undefined
                    ? formatCityRegion(props.dataItem.address.city, props.dataItem.address.stateCode)
                    : ''
                }}
              </td>
            </template>
          </Grid>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.hostEventList .eventTitleLink {
  color: var(--vt-c-teal-dark) !important;
  text-decoration: underline !important;
}
</style>
